// scoring.js — universal scorer for all five SMT/EZ question formats.
// Formats: single, facial, dual, ranking, likert.
// Exports scoreQuestion(q, answer) -> Number (0..q.points, partial credit allowed)
//     and isFullyCorrect(q, answer) -> Boolean (true only at full marks).
//
// Answer shapes produced by the App.js render branch:
//   single  / facial : "B"                       (a letter string)
//   dual             : { best: "C", worst: "A" } (object; either key may be missing mid-answer)
//   ranking          : ["C","D","B","A"]         (array, best -> worst)
//   likert           : { A:3, B:1, C:4, D:2 }    (object, each value 1..4)

const LETTERS = ['A', 'B', 'C', 'D']
const EPS = 1e-9

// Count of correctly-ordered pairs between a proposed ordering and the key ordering.
// Both are arrays of letters, best -> worst. 4 items => 6 unordered pairs.
function orderedPairScore(answerOrder, correctOrder) {
  if (!Array.isArray(answerOrder) || !Array.isArray(correctOrder)) return 0
  const rank = {}
  correctOrder.forEach((letter, i) => { rank[letter] = i })
  let totalPairs = 0, correctPairs = 0
  for (let i = 0; i < answerOrder.length; i++) {
    for (let j = i + 1; j < answerOrder.length; j++) {
      const a = answerOrder[i], b = answerOrder[j]
      if (rank[a] === undefined || rank[b] === undefined) continue
      totalPairs++
      // In the answer, a is placed before b. Correct iff a outranks b in the key.
      if (rank[a] < rank[b]) correctPairs++
    }
  }
  return totalPairs === 0 ? 0 : correctPairs / totalPairs
}

// Fraction of options whose Likert rating is within 1 of the key value.
function likertFraction(answerMap, correctMap) {
  if (!answerMap || typeof answerMap !== 'object') return 0
  const keys = Object.keys(correctMap)
  if (keys.length === 0) return 0
  let within = 0
  keys.forEach(k => {
    const given = answerMap[k]
    if (given == null) return // unrated option scores nothing
    if (Math.abs(Number(given) - Number(correctMap[k])) <= 1) within++
  })
  return within / keys.length
}

export function scoreQuestion(q, answer) {
  const points = q.points || 1
  if (answer == null) return 0

  switch (q.q_type) {
    case 'single':
    case 'facial':
      return answer === q.correct ? points : 0

    case 'dual': {
      // Half credit for the correct best pick, half for the correct worst pick.
      const c = q.correct || {}
      let earned = 0
      if (answer.best && answer.best === c.best) earned += points / 2
      if (answer.worst && answer.worst === c.worst) earned += points / 2
      return earned
    }

    case 'ranking':
      return orderedPairScore(answer, q.correct) * points

    case 'likert':
      return likertFraction(answer, q.correct) * points

    default:
      // Unknown type: fall back to exact match so nothing silently scores wrong.
      return answer === q.correct ? points : 0
  }
}

export function isFullyCorrect(q, answer) {
  if (answer == null) return false
  const points = q.points || 1
  // A question is "fully correct" (green tick) only when it earns full marks.
  return scoreQuestion(q, answer) >= points - EPS
}

// Convenience helpers the review screen uses to render answers per format.
export const SCORING_LETTERS = LETTERS